/**
 * FisheryImage — drop-in <img> wrapper that always shows a vetted UK-carp
 * fishing photo with consistent aesthetics.
 *
 * Features
 *  - Falls back to a curated rotation when no real photo is provided
 *  - On 404 / network error swaps to the platform fallback (no broken icons)
 *  - Optional cinematic overlay for legibility above text
 *  - Lazy loaded by default; pass `priority` for above-the-fold heroes
 *  - Preserves aspect ratio via wrapper (`aspect` prop)
 *
 * Usage:
 *   <FisheryImage seed={lake.id} src={lake.image_url} alt={lake.name} />
 *   <FisheryImage seed={lake.id} variant="hero" overlay />
 */
import { useState } from 'react';
import {
  FISHERY_FALLBACK_IMAGE,
  getFisheryImage,
  getFisheryHero,
} from '../lib/fisheryImages';

const ASPECT_CLASSES = {
  card: 'aspect-[16/10]',
  square: 'aspect-square',
  video: 'aspect-video',
  hero: 'aspect-[21/9]',
  none: '',
};

const FisheryImage = ({
  src,
  seed,
  alt = 'UK carp fishery',
  variant = 'card',      // 'card' | 'hero'
  aspect = 'card',       // 'card' | 'square' | 'video' | 'hero' | 'none'
  overlay = true,
  className = '',
  imgClassName = '',
  priority = false,
  rounded = '',
  testId,
  children,
}) => {
  const initial =
    (typeof src === 'string' && src.trim()) ||
    (variant === 'hero' ? getFisheryHero(seed) : getFisheryImage(seed));

  const [currentSrc, setCurrentSrc] = useState(initial);
  const [errored, setErrored] = useState(false);

  const onError = () => {
    if (errored) return;
    setErrored(true);
    setCurrentSrc(FISHERY_FALLBACK_IMAGE);
  };

  return (
    <div
      className={`relative overflow-hidden bg-[#0b0d0b] ${ASPECT_CLASSES[aspect] || ''} ${rounded} ${className}`}
      data-testid={testId}
    >
      <img
        src={currentSrc}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        onError={onError}
        className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out ${imgClassName}`}
      />
      {overlay && (
        <>
          {/* Bottom gradient — legibility for captions / titles */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent pointer-events-none" />
          {/* Subtle gold vignette — premium aesthetic */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(200,178,115,0.10),transparent_55%)] pointer-events-none" />
        </>
      )}
      {children}
    </div>
  );
};

export default FisheryImage;
